import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  states: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const stateSlice = createSlice({
  name: 'state',
  initialState,
  reducers: {
    setStates: (state, action) => {
      state.states = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setStates, setTotalCount, setTotalPages, setLoading, setError } = stateSlice.actions;

// Get states (paginated, filtered by countryId, & searchable)
export const getStates = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const countryId = filters.countryId || '';
    const response = await axiosInstance.get('/admin/state/list', {
      params: { page, limit, search, countryId }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setStates(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setStates([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch states';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setStates([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch states';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single state details (prefill edit form)
export const getStateDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/state/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch state details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch state details';
    return { status: false, message: errorMsg };
  }
};

// Add new state
export const addState = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/state/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add state' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add state';
    return { status: false, message: errorMsg };
  }
};

// Update state details
export const updateState = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/state/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update state' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update state';
    return { status: false, message: errorMsg };
  }
};

// Delete state
export const deleteState = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/state/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete state' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete state';
    return { status: false, message: errorMsg };
  }
};

export default stateSlice.reducer;
